import React from 'react';

interface LegalSectionProps {
  number: string | number;
  title: string;
  children: React.ReactNode;
}

/**
 * Section of a legal document. Light, Apple-keynote typography to match
 * landing page style (font-display semibold, small ink-muted body).
 */
export const LegalSection: React.FC<LegalSectionProps> = ({ number, title, children }) => {
  return (
    <section>
      <h2 className="font-display font-semibold tracking-tight text-xl text-ink-on-dark mb-3 flex items-baseline gap-3">
        <span className="font-mono text-xs text-brand-primary tracking-[0.2em] shrink-0">
          {String(number).padStart(2, '0')}
        </span>
        {title}
      </h2>
      <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] px-6 py-5 text-sm text-ink-on-dark/85 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
};

interface LegalListProps {
  children: React.ReactNode;
}

export const LegalList: React.FC<LegalListProps> = ({ children }) => (
  <ul className="space-y-2 ml-1">{children}</ul>
);

interface LegalListItemProps {
  children: React.ReactNode;
}

export const LegalListItem: React.FC<LegalListItemProps> = ({ children }) => (
  <li className="flex gap-3 items-start">
    <span className="text-brand-primary mt-1.5 shrink-0 inline-block w-1 h-1 rounded-full bg-brand-primary" aria-hidden />
    <span>{children}</span>
  </li>
);

interface LegalSubsectionProps {
  number: string;
  children: React.ReactNode;
}

/**
 * Numbered sub-paragraph within a section (e.g. "2.1", "2.2").
 * Light-weight, with subtle number prefix to keep document scannable.
 */
export const LegalSubsection: React.FC<LegalSubsectionProps> = ({ number, children }) => (
  <p className="flex gap-3">
    <span className="font-mono text-xs text-ink-on-dark-muted shrink-0 pt-0.5 min-w-[2rem]">
      {number}
    </span>
    <span>{children}</span>
  </p>
);
